<template>
  <div class="tile">
    <b-overlay  :show="!hasData" blur="5px" opacity="1" rounded="lg" style="height: 103%;">
      <MarketReportsMenu  :isEditable="isEditable"
                          :header="header"
                          :hasQuestionBar="true"
                          @edit="handleEdit"
                          @undo="handleUndo"
                          @save="handleSave" />

      <div class="kpi-text">{{ longText }}</div>

      <div class="tile-questions">
        <treeselect class="tile-question-item"
                    :options="questions"
                    v-model="selectedQuestion"
                    :clearable="false"
                    placeholder="KPI ..."
                    style="z-index: auto;" />

        <div class="tile-content" :key="firstBrand">
          <div class="tile-icon">
            <font-awesome-icon  :class="{ 'clickable-disabled': isLeftDisabled, clickable: !isLeftDisabled }"
                                :icon="['fas', 'angle-left']"
                                @click="incrementBrand(-1)" />
          </div>
          <div class="tile-items">
            <QuestionItem v-for="(item, index) in questionItems"
                          :key="index"
                          :data="item"
                          :waves="waves" />
          </div>
          <div class="tile-icon">
            <font-awesome-icon  :class="{ 'clickable-disabled': isRightDisabled, clickable: !isRightDisabled }"
                                :icon="['fas', 'angle-right']"
                                @click="incrementBrand(1)" />
          </div>

          <div class="tile-legend">
            <div  v-for="(wave, index) in getWaves"
                  class="tile-legend-item"
                  :key="index">
              <div :style="{ color: wave.color }">
                <div style="text-align: center;">
                  <font-awesome-icon :icon="['fas', 'square']" height="12" width="12" />
                </div>
                <div style="text-align: center; color: black;">
                  <span style="font-size: 10px;">{{ wave.label }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <template #overlay>
        <div class="text-center">
          <p id="cancel-label">Subjective Questions<br>No data...</p>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import MarketReportsMixin from '@/mixins/MarketReportsMixin';
import MarketReportsAudiMixin from '@/mixins/MarketReportsAudiMixin';
import QuestionItem from '@/components/MarketReports/QuestionItem.vue';

export default {
  mixins: [ MarketReportsAudiMixin, MarketReportsMixin ],

  components: {
    QuestionItem
  },

  props: {
    average:                { type: Array, required: true },
    brandIds:               { type: Array, required: true },
    brandMode:              { type: Boolean, required: true },
    brands:                 { type: Array, required: true },
    questionData:           { type: Array, required: true },
    kpi:                    { type: Array, required: true },
    questions:              { type: Array, required: true },
    waves:                  { type: Array, required: true },
  },

  data() {
    return {
      selectedQuestion: null,
      selectedLongText: null,

      firstBrand: 0
    };
  },

  watch: {
    brandIds() {
      this.firstBrand = 0;
    },

    questions() {
      if (this.questionData.length === 0) {
        return;
      }

      if (!this.questions.find(x => x.id === this.selectedQuestion)) {
        this.selectedQuestion = this.questions[0].id;
        this.selectedLongText = this.kpi.find(x => x.KpiCode === this.selectedQuestion)?.KpiNameLong;
      }

      if (!this.selectedQuestion) {
        this.selectedQuestion = this.questions[0].id;
        this.selectedLongText = this.kpi.find(x => x.KpiCode === this.selectedQuestion)?.KpiNameLong;
      }
    }
  },

  computed: {
    hasData() {
      return this.questionData.length > 0;
    },

    longText() {
      return this.kpi.find(x => x.KpiCode === this.selectedQuestion)?.KpiNameLong;
    },

    questionItems() {
      let results = [];
      if (this.brandMode) {
        let brands = this.getSortedBrandIds(this.brandIds);
        // For each Brand
        for (let i = this.firstBrand; i < this.firstBrand + Math.min(3, brands.length); i++) {
          results.push(this.getFilledItem(brands[i]));
        }
      } else {
        // Only Audi
        results.push(this.getFilledItem(this.brands.find(x => x.isMain).id));
      }
      return results;
    },

    slides() {
      let slideData = [];
      for (let i = 0; i < this.items.length; i += Math.min(3, this.brandIds.length)) {
        slideData.push(this.items.slice(i, i + Math.min(3, this.brandIds.length)));
      }
      return slideData;
    },

    isLeftDisabled() {
      return this.brandIds.length <= 3 || this.firstBrand <= 0;
    },

    isRightDisabled() {
      return this.brandIds.length <= 3 || this.firstBrand + Math.min(3, this.brandIds.length) >= this.brandIds.length;
    },
    getWaves() {
      return this.waves.slice().reverse();
    }
  },

  methods: {
    incrementBrand(inc) {
      if (this.firstBrand === 0 && inc < 0)
        return;

      if (this.firstBrand + 3 >= this.brandIds.length && inc > 0)
        return;

      this.firstBrand += inc;
    },

    getFilledItem(brand) {
      let localWaves = [...this.waves];
      while (localWaves.length < 3) {
        localWaves.push({});
      }
      let result = this.getEmptyItem(localWaves.slice().reverse());
      result.text = this.selectedQuestion;
      result.brand = brand;
      result.logo = this.brands.find(x => x.id === result.brand).logo;
      result.average = this.getAverageValues(localWaves, brand);
      let raw = this.questionData.filter(x => x.KpiCode === this.selectedQuestion && x.Brand === result.brand);
      try {
        for (const item of raw) {
          let star = result.stars.find(x => x.points === item.PointsReached);
          let waveItem = star.waves.find(x => x.id === item.ExternalSurveyWaveId.toString());
          if (waveItem) {
            waveItem.value = item.Percent;
          }
        }
      } catch (error) {
        console.log(error);
      }
      return result;
    },

    getAverageValues(waves, brand) {
      let result = [];
      if (!this.selectedQuestion) return result;
      let kpis = this.average.filter(x => x.Brand === brand && x.KpiCode === this.selectedQuestion);
      for (const wave of waves) {
        let kpi = kpis.find(x => x.ExternalSurveyWaveId === parseInt(wave.id));
        if (kpi) {
          result.push({
            id: parseInt(wave.id),
            color: wave.color,
            rating: kpi && kpi.Result ? kpi.Result : undefined
          })
        } else {
          result.push({
            id: -1,
            color: undefined,
            rating: undefined
          })
        }
      }
      return result.reverse();
    },

    getEmptyItem(waves) {
      let result = { text: this.selectedQuestion, stars: [] };
      for (const point of [1,2,3,4,5]) {
        let item = { points: point, waves: [] };
        for (const wave of waves) {
          item.waves.push(JSON.parse(JSON.stringify(wave)));
        }
        result.stars.push(item);
      }
      return result;
    }
  }
};

</script>

<style lang="scss" scoped>
.kpi-text {
  max-width: 40rem;
  overflow-wrap: break-word;
  padding-left: 1rem;
  top: 0rem;
  max-height: 0rem;
  position: relative;
}
.tile-items {
  height: 100%;
  min-height: 16rem;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: left;
  column-gap: 8rem;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-bottom: 1rem;
}

.carousel {
  height: 100%;
  width: 100%;
  border: 2px solid black;
}

.tile-questions {
  position: relative;
  align-items: start;
  top: -2rem;
}

.tile-question-item {
  width: 32rem;
  margin: auto;
}

.tile-content {
  position: relative;
  top: 4rem;
  display: grid;
  grid-template-columns: 1rem 1fr 1rem;
}

.tile-icon {
  font-size: 2rem;
  margin-top: auto;
  margin-bottom: auto;
}

.tile-legend {
  position: absolute; /* Absolute positioning relative to the container */
  bottom: 1.75rem; /* Align to the bottom */
  right: 2.5rem;

  display: flex;
  justify-content: center; /* Zentriert horizontal */
  align-items: center; /* Zentriert vertikal */
  // height: 100px; /* Setze eine Höhe */
  display: grid;
  grid-template-columns: 6rem;
  column-gap: 1.5rem;
}

.tile-legend-item {
  flex-direction: row;
  justify-content: center;

}

</style>
